import React, { useState, useCallback } from "react";
import "./login.css";
import { supabase } from "../../supabaseClient";
import { useNavigation } from "../../hooks/useNavigation";
import { AppRoute } from "../../types/routes";
import passwordShowIcon from "../../assets/images/passwordshow.png";
import passwordHideIcon from "../../assets/images/passwordhide.png";
import Toast from "../uiComponents/Toast";

const MIN_PASSWORD_LENGTH = 6;
const brandLogo = `${process.env.PUBLIC_URL}/impret-icon.svg`;

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirm?: string;
  }>({});
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
    isVisible: boolean;
  } | null>(null);

  const { replace } = useNavigation();

  const validate = useCallback(() => {
    const newErrors: { password?: string; confirm?: string } = {};

    if (!password) {
      newErrors.password = "Password is required.";
    } else if (password.length < MIN_PASSWORD_LENGTH) {
      newErrors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }

    if (!confirmPassword) {
      newErrors.confirm = "Please confirm your password.";
    } else if (password !== confirmPassword) {
      newErrors.confirm = "Passwords do not match.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [password, confirmPassword]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      setSubmitting(true);
      try {
        const { error } = await supabase.auth.updateUser({
          password,
        });

        if (error) {
          setToast({
            message: error.message,
            type: "error",
            isVisible: true,
          });
          return;
        }

        setToast({
          message: "Password updated successfully. Redirecting to login...",
          type: "success",
          isVisible: true,
        });

        setTimeout(() => replace(AppRoute.LOGIN), 2000);
      } catch {
        setToast({
          message: "Something went wrong. Please try again.",
          type: "error",
          isVisible: true,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [password, validate, replace]
  );

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="brand-logo">
          <img src={brandLogo} alt="Impret" className="brand-logo-mark" />
          Impret
        </div>
        <div className="login-hero-content">
          <h1 className="hero-title">Set your new password</h1>
          <p className="hero-subtitle">
            Choose a strong password to keep your account secure.
          </p>
        </div>
        <div />
      </div>
      <div className="login-right-panel">
        <div className="login-form-wrapper">
          <div className="form-header">
            <h2 className="form-title">Reset Password</h2>
            <p className="form-subtitle">
              Enter your new password below.
            </p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label">New Password</label>
              <div className="input-field-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className={`input-field ${errors.password ? "error" : ""}`}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((p) => !p)}
                >
                  <img
                    src={showPassword ? passwordHideIcon : passwordShowIcon}
                    alt={showPassword ? "Hide password" : "Show password"}
                    style={{ width: 20, height: 20 }}
                  />
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>
            <div className="input-group">
              <label className="input-label">Confirm Password</label>
              <div className="input-field-wrapper">
                <input
                  type={showConfirm ? "text" : "password"}
                  className={`input-field ${errors.confirm ? "error" : ""}`}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm((p) => !p)}
                >
                  <img
                    src={showConfirm ? passwordHideIcon : passwordShowIcon}
                    alt={showConfirm ? "Hide password" : "Show password"}
                    style={{ width: 20, height: 20 }}
                  />
                </button>
              </div>
              {errors.confirm && (
                <span className="error-message">{errors.confirm}</span>
              )}
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={submitting}
              style={{
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Updating..." : "Update Password"}
            </button>
          </form>
          <p className="footer-text">
            Remember your password?
            <span
              className="create-account-link"
              onClick={() => replace(AppRoute.LOGIN)}
            >
              Back to login
            </span>
          </p>
        </div>
      </div>

      {toast?.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  );
};

export default ResetPassword;
